import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Cpu, CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import api from '../../utils/api';

export const meta = {
  id: 'worker_health',
  category: 'system',
  titleKey: 'dashboard.widgets.workerHealth',
  descriptionKey: 'dashboard.widgets.workerHealthDesc',
  defaultSize: 'sm',
  minRole: 'admin',
  icon: Cpu,
};

export default function WorkerHealthWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['worker-health-heartbeats'],
    queryFn: () => api.get('/worker-health/heartbeats').then(r => r.data),
    refetchInterval: 60 * 1000,
  });

  const summary = data?.summary;
  if (!summary || summary.total === 0) {
    return (
      <div className="card" style={{ height: '100%' }}>
        <div className="card-header">
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Cpu size={16} />
            {t('dashboard.widgets.workerHealth')}
          </div>
        </div>
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>—</div>
      </div>
    );
  }

  // Roll erroring + stale into one "bad" line for the at-a-glance view
  const bad = (summary.erroring || 0) + (summary.stale || 0);

  return (
    <div className="card" style={{ cursor: 'pointer', height: '100%' }} onClick={() => navigate('/worker-health')}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Cpu size={16} />
          {t('dashboard.widgets.workerHealth')}
        </div>
      </div>
      <div style={{ padding: '12px 20px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {bad > 0 && (
          <Row Icon={XCircle} color="#ef4444"
               label={t('dashboard.widgets.workerStaleOrErroring', 'Stale / erroring')} value={bad} />
        )}
        {summary.at_risk > 0 && (
          <Row Icon={AlertTriangle} color="#f59e0b"
               label={t('dashboard.widgets.workerAtRisk', 'At risk')} value={summary.at_risk} />
        )}
        <Row Icon={CheckCircle2} color="#22c55e"
             label={t('dashboard.widgets.workerHealthy', 'Healthy')} value={summary.healthy} />
        {summary.never_ran > 0 && (
          <Row Icon={HelpCircle} color="#6b7280"
               label={t('dashboard.widgets.workerNeverRan', 'Never ran')} value={summary.never_ran} />
        )}
        <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{t('dashboard.widgets.workerTotal', 'Total workers')}</span>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{summary.total}</span>
        </div>
      </div>
    </div>
  );
}

function Row({ Icon, color, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Icon size={14} color={color} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 13, flex: 1 }}>{label}</span>
      <span style={{ fontSize: 18, fontWeight: 700, color }}>{value}</span>
    </div>
  );
}
